import { Dispatch, SetStateAction, memo, useEffect, useState } from 'react'
import type {
  Product,
  ProductOption,
  ProductOptionValues,
  ProductSize,
  ProductVariant,
} from '@commerce/types/product'
import { SelectedOptions } from '../helpers'
import VariantRound from '@components/yadav/ui/VariantRound'
import VariantRectangle from '@components/yadav/ui/VariantRectangle'
import VariantSquare from '@components/yadav/ui/VariantSquare'
import { VariantCollapse } from '@components/yadav/ui'
import {
  formatOption,
  getAllAvailableFilters,
  getAllVariants,
  getVariantsFor,
} from './helpers'
import cn from 'clsx'
import {
  getMinSize,
  getPriceDifference,
  getSizeStep,
} from '../ProductDescription/helpers'

interface ProductOptionsProps {
  product: Product
  productVariant?: ProductVariant
  options?: ProductOption[]
  setSelectedOptions: Dispatch<SetStateAction<SelectedOptions>>
  className?: string
  type: 'METAL' | 'DIAMOND_TYPE' | 'TCW' | 'SIZE'
  sizeOptions?: ProductSize | ProductSize[];
  productType?: string
  setVariantSettings: Dispatch<
    SetStateAction<{
      metal: string
      diam_type: string
      tcw: string
      size: string
    }>
  >
  variantSettings: {
    metal: string
    diam_type: string
    tcw: string
    size: string
  }
  selectedSizeValue?: string
  setSelectedSizeValue?: Dispatch<SetStateAction<string>>
}

const ProductOptions: React.FC<ProductOptionsProps> = ({
  product,
  productVariant,
  options,
  setSelectedOptions,
  className,
  type,
  sizeOptions,
  productType,
  setVariantSettings,
  variantSettings,
  selectedSizeValue,
  setSelectedSizeValue,
}) => {

  const allSizes = Array.isArray(sizeOptions)
      ? sizeOptions
        .flatMap((group) =>
          group.available_sizes.map((size) => ({
            size: parseFloat(size),
            priceAdjustment: String(group.price_adjustment),
          }))
        )
        .sort((a, b) => a.size - b.size)
      : sizeOptions?.available_sizes
        .map((size) => ({
          size: parseFloat(size),
          priceAdjustment: String(sizeOptions.price_adjustment),
        }))
        .sort((a, b) => a.size - b.size) || [];

  // Preselect the first available size when the product variant changes
  useEffect(() => {
    if (allSizes.length > 0) {
      if(setSelectedSizeValue) setSelectedSizeValue(allSizes[0].size.toString());
    } else {
      if(setSelectedSizeValue) setSelectedSizeValue('');
    }
  }, [productVariant]);

  if (!options) {
    return null
  } else {
    const opt = formatOption(options, type)
    const allVariants = getAllVariants(product, variantSettings.metal)
    const currentFilters = getAllAvailableFilters(allVariants)
    const sizes = Array.isArray(sizeOptions)
      ? sizeOptions.flatMap((group) => group.available_sizes.map((size) => ({
        size,
        priceAdjustment: group.price_adjustment,
        sizeType: group.size_type,
      })))
      : sizeOptions?.available_sizes.map((size) => ({
        size,
        priceAdjustment: sizeOptions.price_adjustment,
        sizeType: sizeOptions.size_type,
      }));

    const diamTypeVariants = getVariantsFor(
      'diam_type',
      allVariants,
      variantSettings,
      currentFilters
    )

    const tcwVariants = getVariantsFor(
      'tcw',
      allVariants,
      variantSettings,
      currentFilters
    )

    const sizeVariants = getVariantsFor(
      'size',
      allVariants,
      variantSettings,
      currentFilters
    )

    const canRenderMetalOptions =
      type === 'METAL' &&
      product !== undefined &&
      productVariant !== undefined &&
      setSelectedOptions !== undefined &&
      opt !== undefined

    const canRenderDiamondTypeOptions =
      type === 'DIAMOND_TYPE' &&
      productType !== undefined &&
      diamTypeVariants.length > 0

    const canRenderTCWOptions = type === 'TCW' && tcwVariants.length > 0

    // Size Selectors
    const canRenderBandSizeSelector =
      type === 'SIZE' && sizes && sizes.length > 0

    const canRenderRestSizeSelector = type === 'SIZE' && sizeVariants.length > 0

    return canRenderMetalOptions ? (
      <VariantRound
        optionType={type.toString()}
        optionValue={productVariant?.options[0]?.values[0]?.label}
        opt={opt}
        className={'my-6'}
        key={opt.displayName}
        setSelectedOptions={setSelectedOptions}
        product={product}
        variantSettings={variantSettings}
        setVariantSettings={setVariantSettings}
        currentFilters={currentFilters}
      />
    ) : canRenderDiamondTypeOptions ? (
      <VariantRectangle
        product={product}
        setVariantSettings={setVariantSettings}
        variantSettings={variantSettings}
        className={'my-6'}
        variants={diamTypeVariants}
        key={opt?.displayName}
        optionType={type.toString().split('_').join(' ')}
        productType={productType ?? ''}
        currentFilters={currentFilters}
      />
    ) : canRenderTCWOptions ? (
      <VariantSquare
        setVariantSettings={setVariantSettings}
        variantSettings={variantSettings}
        variants={tcwVariants}
        optionType={type.toString()}
        className="my-6"
        key={opt?.displayName}
        product={product}
        currentFilters={currentFilters}
        productType={productType}
      />
    ) : canRenderBandSizeSelector ? (
      <div className="flex flex-col gap-4 my-6">
        <div className="flex gap-1">
          <span className="font-bold text-base capitalize">
            {type.toLowerCase()}:
          </span>
          {/* <span className="font-normal text-base">
            {selectedSizeValue
              ? `${selectedSizeValue}${allSizes.findIndex((item) => item.size.toString() === selectedSizeValue) === 0
                ? '' // Does not show the priceAdjustment if it is the first option
                : ` (+ ${allSizes.find((item) => item.size.toString() === selectedSizeValue)?.priceAdjustment || "0"
                } USD)`
              }`
              : "Select a size"}
          </span> */}
          <span className="font-normal text-base">
            {selectedSizeValue ? (
              (() => {
                const selected = allSizes.find(
                  (item) =>
                    item.size.toString() === Number(selectedSizeValue).toString()
                );
                const priceAdjustment = selected?.priceAdjustment || 0;
              
                return (
                  <>
                    {Number(selectedSizeValue).toString()}
                    {Number(priceAdjustment) > 0 && ` (+ ${priceAdjustment} USD)`}
                  </>
                );
              })()
            ) : (
              "Select a size"
            )}
          </span>
        </div>
        <VariantCollapse
          optionType={type.toString()}
          optionValue=            
            /* {selectedSizeValue
              ? `${selectedSizeValue}${allSizes.findIndex((item) => item.size.toString() === selectedSizeValue) === 0
                ? '' // Does not show the priceAdjustment if it is the first option
                : ` (+ ${allSizes.find((item) => item.size.toString() === selectedSizeValue)?.priceAdjustment || "0"
                } USD)`
              }`
              : "Select a size"} */
              {
                selectedSizeValue
                  ? (() => {
                      const priceAdjustment =  allSizes.find(
                        (item) =>
                          item.size.toString() === Number(selectedSizeValue).toString()
                      )?.priceAdjustment ?? 0
                      return `${Number(selectedSizeValue)}${Number(priceAdjustment) > 0 ? ` (+ ${priceAdjustment} USD)` : ''}`
                    })()
                  : 'Select a size'
              }

          className="w-full lg:ml-0"
        >
          <div
            role="listbox"
            className="flex flex-col justify-between p-2 max-h-[240px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100"
          >
            {allSizes.map((item, i) => {
              // Format the value depending on whether it is the first option
              const formattedValue =
            /*  i === 0
                  ? `${item.size.toFixed(2)}` // Only show the size for the first option
                  : `${item.size.toFixed(2)} (+ ${item.priceAdjustment} USD)`; // Includes adjustment for the others */
                Number(item.priceAdjustment) > 0
                  ? `${item.size} (+ ${item.priceAdjustment} USD)`
                  : `${item.size}`;
              const handleOnSelectedSize = () => {
                if (setSelectedSizeValue) setSelectedSizeValue(item.size.toString());
              };

              return (
                <span
                  onClick={handleOnSelectedSize}
                  key={`${item.size}-${i}`}
                  className={cn(
                    'w-full hover:bg-neutral-100 py-3 text-sm transition-all cursor-pointer text-center',
                    {
                      'bg-neutral-50': selectedSizeValue === item.size.toString(),
                    }
                  )}
                >
                  {formattedValue}
                </span>
              );
            })}
          </div>
        </VariantCollapse>
      </div>

    ) : canRenderRestSizeSelector ? (
      <VariantSquare
        setVariantSettings={setVariantSettings}
        variantSettings={variantSettings}
        variants={sizeVariants}
        optionType={type.toString()}
        className="my-6"
        key={opt.displayName}
        product={product}
        currentFilters={currentFilters}
      />
    ) : null
  }
}

export default memo(ProductOptions)
